import React from 'react';

const CANCELLED_PROMISE = '_cancelled_';

class UnmountablePureComponent<Props = any, State = any> extends React.PureComponent<Props, State> {
    /*
    This component is useful to extend if it calls setState inside an asynchronous
    action and is possible to be unmounted before completing. For example, if a
    component makes an ajax request, and could be unmounted before the request completes,
    and calls setState in the "success" or "error" callbacks. Normally, that would result
    in an error - you can't call setState on an unmounted component. Extending this
    component makes it possible to avoid that error.

    This component provides the following methods, to be used by child components:

    - _push_promise -> make a promise that can be cancelled if the component is unmounted
    - _is_cancelled(val) -> detect if the value indicates that the promise was
                            cancelled because the component was unmounted

    To extend, just use the following code where the asynchronous action happens (an
    ajax example is given, but it could also be with window.setTimeout, or any other
    async code):

    {# EXAMPLE #}
    const ajax_promise = $.ajax({...});

    // push the promise into the list that UnmountablePureComponent maintains:
    const cancellable_promise = this._push_promise(ajax_promise);

    // cancellable_promise is a new promise, hook up the success/error callbacks
    // to this promise, rather than putting them in the ajax call
    cancellable_promise.done((response) => {
        // the ajax data is returned through the param
        this.setState({
            data: response
        });
    });

    cancellable_promise.fail((err) => {
        if (this._is_cancelled(err)) {
            // the component was unmounted, handle that case
            ...
        }
        else {
            // the ajax request failed (non-200 status, or something), handle that case
            ...
        }
    });
    */
    promises: any[];

    constructor(props: any) {
        super(props);

        this.promises = [];
    }

    _push_promise(promise: any) {
        const cancelable_promise = this.makeCancelable(promise);
        this.promises.push(cancelable_promise);
        return cancelable_promise.promise.promise();
    }

    _remove_promise(promise: any) {
        let index = -1;
        for (let i = 0; i < this.promises.length; i++) {
            if (this.promises[i].promise === promise) {
                index = i;
                break;
            }
        }

        if (index > -1) {
            this.promises.splice(index, 1);
        }
    }

    _is_cancelled(error: any) {
        return error?.message === CANCELLED_PROMISE;
    }

    override componentWillUnmount() {
        for (let i = 0; i < this.promises.length; i++) {
            this.promises[i].cancel();
        }
    }

    makeCancelable(promise: any) {
        let hasCanceled_ = false;

        const wrappedPromise = $.Deferred();
        promise.then(
            (val: any) => {
                this._remove_promise(wrappedPromise);
                hasCanceled_
                    ? wrappedPromise.reject(new Error(CANCELLED_PROMISE))
                    : wrappedPromise.resolve(val);
            },
            (error: any) => {
                this._remove_promise(wrappedPromise);
                hasCanceled_
                    ? wrappedPromise.reject(new Error(CANCELLED_PROMISE))
                    : wrappedPromise.reject(error);
            },
        );

        return {
            promise: wrappedPromise,
            cancel() {
                hasCanceled_ = true;
            },
        };
    }
}

export default UnmountablePureComponent;
